import { startCase, uniq } from 'lodash-es'
import { ReactGoogleChartProps } from 'react-google-charts'
import { ParsedChartProps } from 'types'
import { useTheme } from 'utils'

export const defaultChartProps: Partial<ReactGoogleChartProps> = {
  width: '100%',
  height: '100%',
}

export const customColor = (label: any) => {
  switch (label) {
    case 'hot':
      return '#E83A5A'
    case 'cold':
      return '#77B6FF'
    case 'warm':
      return '#E8C23A'
    case null:
      return '#bbbbbb'
  }
}

export function makeDefaultChartOptions(props: ParsedChartProps): ReactGoogleChartProps['options'] {
  const { legend } = props
  const { theme: colorMode } = useTheme()

  return {
    animation: { duration: 1000, easing: 'out' },
    backgroundColor: '',
    colors: ['#3C4BD3', '#E77522', '#7E4CE1', '#0BBB67', '#1977E5'],
    curveType: 'function',
    is3D: false,
    legend: {
      position: legend?.show ? 'end' : 'none',
      textStyle: {
        fontName: 'system-ui',
        color: colorMode === 'light' ? '#333' : '#FFF',
      },
    },
    hAxis: {
      viewWindow: {
        min: 0,
      },
      textStyle: {
        color: colorMode === 'light' ? '#333' : '#CCC',
      },
      gridlines: {
        color: colorMode === 'light' ? '#c3c3c3' : '#999999',
      },
      minorGridlines: {
        color: colorMode === 'light' ? '#e1e1e1' : '#707070',
      },
      baselineColor: colorMode === 'light' ? '#333' : '#FFF',
      slantedText: false,
      showTextEvery: 1,
    },
    vAxis: {
      viewWindow: {
        min: 0,
      },
      textStyle: {
        color: colorMode === 'light' ? '#333' : '#CCC',
      },
      gridlines: {
        color: colorMode === 'light' ? '#c3c3c3' : '#999999',
      },
      minorGridlines: {
        color: colorMode === 'light' ? '#e1e1e1' : '#707070',
      },
      baselineColor: colorMode === 'light' ? '#333' : '#FFF',
      slantedText: false,
      showTextEvery: 1,
    },
    tooltip: {
      isHtml: true,
    },
  }
}

export function makeChartData(props: ParsedChartProps) {
  const { data, values, categories, categoryInfo } = props
  const firstCategory = categories[0] || 'Label'
  const primaryFormatter = categoryInfo?.[0]?.formatter

  if (categories[1]) {
    const primaryCategories = uniq(data.map(d => d[categories[0]])) as string[]
    const secondaryFormatter = categoryInfo?.[1]?.formatter
    const secondaryCategories = uniq(data.map(d => d[categories[1]])) as string[]
    const header = ['Label', ...secondaryCategories.map(c => formatCategory(c, secondaryFormatter))]
    const body = primaryCategories.map(category => {
      const valueData = secondaryCategories.map(secondaryCategory => {
        const row = data.find(
          d => d[categories[0]] === category && d[categories[1]] === secondaryCategory
        )
        return row?.[values[0].field] || 0
      })
      return [formatCategory(category, primaryFormatter), ...valueData]
    })
    return [header, ...body] as any[][]
  } else {
    const header = ['Label', ...values.map(v => v.label || ``)]
    const body = data.map(d => {
      return [formatCategory(d[firstCategory], primaryFormatter), ...values.map(s => d[s.field])]
    })
    return [header, ...body] as any[][]
  }
}

function formatCategory(value: any, formatter?: (value: any) => string) {
  if (formatter) return formatter(value)
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  if (!value && value !== 0) return 'Unset'
  return startCase(String(value))
}

export const transposeData = (data: any[][]) =>
  data[0].map((_, colIndex) => data.map(row => row[colIndex]))
